// Consent Banners - create, behaviour, regulations, appearance, event activity, consent proof, GCM, delete.

import { ROUTES } from 'src/router/const'
import {
  BULLET_VARIANTS,
  CHECKLIST_TONES,
  FAQ_BLOCK_TYPES,
  FAQ_PREVIEW_VARIANTS,
  FAQ_SECTION_ICON_TONES,
} from 'src/services/consts/faq/content.const'

/** @type {import('src/services/helpers/faq/content.helper').FaqTopic} */
export const CONSENT_BANNERS_TOPIC = {
  id: 'consent-banners',
  title: 'Consent Banners',
  icon: 'layout-panel-top',
  color: 'cyan',
  fill: 'cyan-light',
  description:
    'Create and publish the banner visitors see. Set behaviour, regulations, and appearance, then read event activity.',
  sections: [
    {
      title: 'Create and manage',
      items: [
        { articleId: 'create-a-banner', icon: 'plus' },
        { articleId: 'delete-a-banner', icon: 'trash' },
      ],
    },
    {
      title: 'Behaviour and publishing',
      items: [{ articleId: 'configure-banner-behaviour', icon: 'sliders-horizontal' }],
    },
    {
      title: 'Regulations and visibility',
      items: [{ articleId: 'choose-your-regulations', icon: 'shield-check' }],
    },
    {
      title: 'Appearance',
      items: [{ articleId: 'customize-appearance', icon: 'palette' }],
    },
    {
      title: 'Event activity',
      items: [
        { articleId: 'read-banner-event-stats', icon: 'chart-column' },
        { articleId: 'diagnose-missing-events', icon: 'search-x' },
      ],
    },
    {
      title: 'Consent proof',
      items: [{ articleId: 'verify-consent-record', icon: 'badge-check' }],
    },
    {
      title: 'Google tags',
      items: [{ articleId: 'google-consent-mode-setting', icon: 'sliders-horizontal' }],
    },
  ],
}

/** @type {Record<string, import('src/services/helpers/faq/content.helper').FaqArticle>} */
export const CONSENT_BANNERS_ARTICLES = {
  'create-a-banner': {
    id: 'create-a-banner',
    title: 'Create a banner',
    description:
      'Learn what every banner setting does, when it goes live, and how to recover when a domain is invalid or already taken.',
    keywords: [
      'new banner',
      'add site',
      'scan website immediately',
      'banner name',
      'website url',
      'additional domains',
      'alias domains',
      'alias domain',
      'regional domain',
      'alternate url',
      'privacy policy url',
      'default language',
      'locale',
      'duplicate domain',
      'already exists',
      'available languages',
      'allowed languages',
      'language selector',
    ],
    lastUpdated: '2026-10-09',
    content: [
      {
        id: 'create-a-banner-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Learn what every setting does when you create a banner, and what to try if creation fails.',
      },
      {
        id: 'create-a-banner-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Each banner goes live for one website the moment you create it, and the banner settings can be adjusted later.',
      },
      {
        id: 'create-a-banner-video-section',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'See a banner created',
        icon: 'file-video',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'create-a-banner-video-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A short walkthrough of creating a banner.',
          },
          {
            id: 'create-a-banner-video',
            type: FAQ_BLOCK_TYPES.VIDEO,
            videoId: '1223430840',
            hash: '1e2872adda',
          },
        ],
      },
      {
        id: 'create-a-banner-walkthrough',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'The four steps',
        icon: 'compass',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'create-a-banner-walkthrough-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A quick overview of each step in the banner creation flow. The next section covers every setting in depth.',
          },
          {
            id: 'create-a-banner-walkthrough-steps',
            type: FAQ_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'create-a-banner-walkthrough-regulations',
                title: 'Regulations',
                text: 'Pick the regulations that match where your visitors are - {regulation:gdpr}, {regulation:ccpa}, or {regulation:notice}. Each visitor sees the one that matches their country.',
              },
              {
                id: 'create-a-banner-walkthrough-details',
                title: 'Details',
                text: 'Give the banner an internal Banner Name and the Website URL it runs on. Additional Domains and Description are optional.',
              },
              {
                id: 'create-a-banner-walkthrough-configure',
                title: 'Configure',
                text: "Choose the banner's behaviour: coverage for countries outside your regulations, whether to scan the website on create, Google Consent Mode, languages, and an optional Privacy Policy link. Confirming here creates and saves the banner.",
              },
              {
                id: 'create-a-banner-walkthrough-done',
                title: 'Done',
                text: '[[Install the script|install-the-script]] on your site so visitors see the banner. If Scan was on, it is already running in the background.',
              },
            ],
          },
        ],
      },
      {
        id: 'create-a-banner-settings',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Every banner setting',
        icon: 'settings',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'create-a-banner-settings-bullets',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'create-a-banner-setting-regulations',
                icon: 'shield-check',
                title: 'Regulations',
                text: "The [[regulations you pick|choose-your-regulations]] decide which banner variant each visitor sees based on their country. {regulation:notice} covers everyone your other regulations don't. Edit later from [[the Behaviour tab|configure-banner-behaviour]].",
              },
              {
                id: 'create-a-banner-setting-banner-name',
                icon: 'pencil',
                title: 'Banner Name',
                text: 'A name just for you to recognize this banner. Visitors never see it. Required when you create the banner. Edit later from the Overview tab.',
              },
              {
                id: 'create-a-banner-setting-website-url',
                icon: 'link',
                title: 'Website URL',
                text: 'Each website URL in your account can have one banner. Edit later from the Overview tab - changing it requires confirming {btn:save-and-scan}.',
              },
              {
                id: 'create-a-banner-setting-additional-domains',
                icon: 'globe',
                title: 'Additional Domains',
                text: 'Optional extra hosts (regional, alternate, or test) that run this same banner. Invalid or duplicate entries are rejected; valid ones go live with the banner. Edit later from the Overview tab.',
              },
              {
                id: 'create-a-banner-setting-description',
                icon: 'file-text',
                title: 'Description',
                text: 'Optional team notes. Visitors never see them. Edit later from the Overview tab.',
              },
              {
                id: 'create-a-banner-setting-rest-of-the-world',
                icon: 'route',
                title: 'Rest of the World',
                text: "What visitors from countries outside [[your selected regulations|choose-your-regulations]] see - another regulation's rules, {regulation:notice}, or no banner. Edit later from [[the Behaviour tab|configure-banner-behaviour]].",
              },
              {
                id: 'create-a-banner-setting-scan',
                icon: 'scan-search',
                title: 'Scan website immediately',
                text: "On by default, and available **only during creation** - it isn't on the Behaviour tab later. When on, [[a scan starts right away|run-a-scan]]; first results appears shortly, with more arriving in the background. Later scans run from the Cookies & Trackers tab.",
              },
              {
                id: 'create-a-banner-setting-gcm',
                icon: 'sliders-horizontal',
                title: 'Google Consent Mode',
                text: '[[Google Consent Mode|google-consent-mode-setting]] is on by default and live as soon as you create the banner. Edit later from [[the Behaviour tab|configure-banner-behaviour]].',
              },
              {
                id: 'create-a-banner-setting-available-languages',
                icon: 'list-checks',
                title: 'Available Languages',
                text: 'Pick the languages visitors can see the banner in. Choose **Select all** to include every current and future language. Picking just one hides the language selector on the banner. Edit later from [[the Behaviour tab|configure-banner-behaviour]].',
              },
              {
                id: 'create-a-banner-setting-default-language',
                icon: 'languages',
                title: 'Default Language',
                text: "The fallback shown when a visitor's browser language is unknown. Defaults to English (`en`). Edit later from [[the Behaviour tab|configure-banner-behaviour]].",
              },
              {
                id: 'create-a-banner-setting-privacy-url',
                icon: 'lock',
                title: 'Privacy Policy URL',
                text: 'Optional. When set, the banner links to it. [[Compliance Health|understand-your-overview]] treats that URL as the privacy-policy signal (presence only). Edit later from [[the Behaviour tab|configure-banner-behaviour]].',
              },
              {
                id: 'create-a-banner-setting-appearance',
                icon: 'palette',
                title: 'Appearance',
                text: 'Not set when you create the banner - Cookie Armor assigns product defaults after the banner exists. Edit later from [[the Appearance tab|customize-appearance]].',
              },
            ],
          },
          {
            id: 'create-a-banner-settings-live-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'info',
            text: 'These values go live as soon as you create the banner. Visitors see them only after you [[install the script|install-the-script]].',
          },
        ],
      },
      {
        id: 'create-a-banner-if-it-fails',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Why creating a banner might fail',
        icon: 'alert-triangle',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'create-a-banner-if-it-fails-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            tone: CHECKLIST_TONES.NEGATIVE,
            items: [
              {
                id: 'create-a-banner-if-it-fails-primary',
                text: '**Primary URL already in use** - a URL that already has a banner in your account is rejected. Use a different URL, or [[delete the existing banner|delete-a-banner]] and create again for that domain.',
              },
              {
                id: 'create-a-banner-if-it-fails-additional',
                text: '**Additional Domains are invalid or duplicate** - entries that are invalid, duplicate another Additional Domain, or match the primary URL block creation until you fix them.',
              },
              {
                id: 'create-a-banner-if-it-fails-retry',
                text: '**Anything else** - try again, or [[contact support|contact-support]] if the problem persists.',
              },
            ],
          },
        ],
      },
    ],
  },

  'configure-banner-behaviour': {
    id: 'configure-banner-behaviour',
    title: 'Configure banner behaviour',
    description:
      "Change a banner's regulations, languages, privacy policy link, and Google Consent Mode, and publish the draft.",
    keywords: [
      'Publish changes',
      'Discard',
      'unsaved changes',
      'draft',
      'behaviour',
      'Consent Behavior',
      'default language',
      'privacy policy url',
      'fallback language',
      'available languages',
      'allowed languages',
      'language selector',
      'publish disabled',
      "can't publish",
      'Rest of the World',
      'catch-all',
    ],
    lastUpdated: '2026-10-13',
    content: [
      {
        id: 'cbb-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: "Change the banner's settings: regulations, languages, privacy policy link and more.",
      },
      {
        id: 'cbb-video-section',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'See behaviour configured',
        icon: 'file-video',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'cbb-video-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A short walkthrough of configuring banner behaviour.',
          },
          {
            id: 'cbb-video',
            type: FAQ_BLOCK_TYPES.VIDEO,
            videoId: '1223430839',
            hash: 'f2b0785094',
          },
        ],
      },
      {
        id: 'cbb-fields',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What each setting does',
        icon: 'sliders-horizontal',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'cbb-fields-bullets',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'cbb-fields-regulations',
                icon: 'shield-check',
                title: 'Regulations',
                text: "The [[regulations you pick|choose-your-regulations]] decide which banner variant each visitor sees based on their country. {regulation:notice} covers everyone your other regulations don't.",
              },
              {
                id: 'cbb-fields-rest-of-the-world',
                icon: 'route',
                title: 'Rest of the World',
                text: "What visitors from countries outside [[your selected regulations|choose-your-regulations]] see - another regulation's rules, {regulation:notice}, or no banner.",
              },
              {
                id: 'cbb-fields-gcm',
                icon: 'sliders-horizontal',
                title: 'Google Consent Mode',
                text: '[[Google Consent Mode|google-consent-mode-setting]] decides whether Cookie Armor writes Google tag consent from banner choices.',
              },
              {
                id: 'cbb-fields-available-languages',
                icon: 'list-checks',
                title: 'Available languages',
                text: 'Pick the languages visitors can see the banner in. Choose **Select all** to include every current and future language. Picking just one hides the language selector on the banner.',
              },
              {
                id: 'cbb-fields-language',
                icon: 'languages',
                title: 'Default Language',
                text: "The fallback shown when a visitor's browser language is unknown. Defaults to English (`en`).",
              },
              {
                id: 'cbb-fields-privacy',
                icon: 'lock',
                title: 'Privacy Policy URL',
                text: 'Optional. When set, the banner links to it. [[Compliance Health|understand-your-overview]] treats that URL as the privacy-policy signal (presence only).',
              },
            ],
          },
          {
            id: 'cbb-fields-publish-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'info',
            text: 'Changes stay in the form until you click {btn:publish-changes}.',
          },
        ],
      },
      {
        id: 'cbb-buttons',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Publish or discard your changes',
        icon: 'send',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'cbb-buttons-intro',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Two buttons sit at the bottom of the form.',
          },
          {
            id: 'cbb-buttons-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'cbb-buttons-publish',
                text: '{btn:publish-changes} applies the draft, and visitors see the new behaviour. The button is enabled only after something changes. If it stays disabled, some fields still need a value - hover over the button to see which.',
              },
              {
                id: 'cbb-buttons-discard',
                text: '{btn:discard} restores the last saved values. The button is enabled as soon as something in the form changes.',
              },
            ],
          },
        ],
      },
    ],
  },

  'choose-your-regulations': {
    id: 'choose-your-regulations',
    title: 'Choose your regulations',
    description:
      'Pick which regulations your banner uses and decide what visitors outside those regions see.',
    keywords: [
      'privacy regulations',
      'wrong regulation',
      'wrong banner',
      'incorrect regulation',
      'which banner',
      'banner not showing',
      'script installed',
    ],
    lastUpdated: '2026-10-13',
    content: [
      {
        id: 'choose-your-regulations-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'The regulations you select decide which banner each visitor sees. Use this when you are picking regulations for a new banner, changing what visitors outside those regions see, or figuring out why a country sees a different variant than you expected.',
      },
      {
        id: 'choose-your-regulations-legal',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'These options configure product behaviour. Cookie Armor does not give legal advice. Whether you need a banner, and what consent and [[ePrivacy / cookie law|eprivacy-cookie-law]] require, is in [[Do I need a cookie banner?|do-i-need-a-cookie-banner]].',
      },
      {
        id: 'choose-your-regulations-at-a-glance',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Each regulation at a glance',
        icon: 'shield-check',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'choose-your-regulations-variants',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'choose-your-regulations-variants-gdpr',
                title: '{regulation:gdpr}',
                text: 'Covers visitors in the EU, EEA and the UK. Accept, reject, and manage preferences (when the Manage preferences button is on in [[Appearance|customize-appearance]]). Optional categories stay blocked until the visitor chooses.',
              },
              {
                id: 'choose-your-regulations-variants-ccpa',
                title: '{regulation:ccpa}',
                text: 'Covers visitors in the United States (US). Agree, plus a Do Not Sell or Share choice. Optional categories run until the visitor opts out.',
              },
              {
                id: 'choose-your-regulations-variants-notice',
                title: '{regulation:notice}',
                text: 'Covers whoever your other regulations do not. Discloses cookies and never blocks scripts. I Agree records an all-granted consent. Close dismisses without an event. There is no Reject All or Custom choice.',
              },
            ],
          },
        ],
      },
      {
        id: 'choose-your-regulations-rotw',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Rest of the World',
        icon: 'globe',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'choose-your-regulations-rotw-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Rest of the World is what visitors outside your selected regions see. The options are a {regulation:notice} banner, the same banner you show {regulation:gdpr} or {regulation:ccpa} visitors, or no banner at all.',
          },
          {
            id: 'choose-your-regulations-rotw-later',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If you later remove the regulation your Rest of the World choice depended on, you need to pick another Rest of the World option.',
          },
        ],
      },
      {
        id: 'choose-your-regulations-who',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Who sees which variant',
        icon: 'compass',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'choose-your-regulations-who-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "After the script loads, Cookie Armor resolves the visitor's country from the Content Delivery Network (CDN). It does not use the browser geolocation prompt.",
          },
          {
            id: 'choose-your-regulations-journey',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.JOURNEY,
            items: [
              {
                id: 'choose-your-regulations-journey-geo',
                icon: 'globe',
                title: 'Resolve country',
                text: 'Cookie Armor reads the country from the CDN.',
              },
              {
                id: 'choose-your-regulations-journey-exact',
                icon: 'search-check',
                title: 'Exact country match',
                text: "If the country is in a selected regulation's list - for example Germany (DE) in GDPR, United States (US) in CCPA - that regulation wins.",
              },
              {
                id: 'choose-your-regulations-journey-rotw',
                icon: 'route',
                title: 'Rest of the World',
                text: 'If no list contains the country, your Rest of the World choice applies - GDPR rules, CCPA rules, Cookie Notice, or No banner. No banner hides the consent UI for that visit.',
              },
            ],
          },
          {
            id: 'choose-your-regulations-fail-open',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'info',
            text: 'If the country cannot be resolved, Cookie Armor fails open and still shows a banner. "No banner" hides the banner only for a resolved country outside the regions you selected.',
          },
          {
            id: 'choose-your-regulations-install-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'A verified install can still hide the banner',
            text: 'A {status:script-installed} status means Cookie Armor can see the script on your pages, not that every visitor sees a banner. If Rest of the World is No banner, that is a regulation choice and the [[install|install-the-script]] is still working.',
          },
          {
            id: 'choose-your-regulations-examples',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Selection', 'DE', 'US', 'JP'],
            rows: [
              ['GDPR; Rest of the World: GDPR rules', 'GDPR', 'GDPR', 'GDPR'],
              ['GDPR; Rest of the World: No banner', 'GDPR', 'No banner', 'No banner'],
              ['GDPR + CCPA; Rest of the World: Cookie Notice', 'GDPR', 'CCPA', 'Cookie Notice'],
              ['GDPR + CCPA; Rest of the World: GDPR rules', 'GDPR', 'CCPA', 'GDPR'],
            ],
          },
        ],
      },
      {
        id: 'choose-your-regulations-coming-soon',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Coming Soon',
        icon: 'sparkles',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'choose-your-regulations-coming-soon-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '{regulation:lgpd} (Brazil) and {regulation:pipeda} (Canada) appear as options but are not available yet - they cannot be selected. Visitors in those regions follow your Rest of the World choice, like any country your selected regulations do not cover.',
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'choose-your-regulations-faq-one',
        question: 'Do I have to select more than one regulation?',
        answer:
          'No. One regional law covers visitors in that region. You still choose Rest of the World for everyone else - extend that banner, show a Cookie Notice, or show no banner.',
      },
      {
        id: 'choose-your-regulations-faq-vpn',
        question: 'What if a visitor uses a VPN?',
        answer:
          'The country comes from the CDN view of the request. Cookie Armor does not try to detect a Virtual Private Network (VPN) or change the country.',
      },
    ],
  },

  'customize-appearance': {
    id: 'customize-appearance',
    title: 'Customize appearance',
    description:
      'Appearance choices change what visitors see once you publish. Blocking overlay is visual only, Preview does not override the country match, and Reset writes product defaults the moment you confirm.',
    keywords: [
      'hex',
      'dark mode',
      'position',
      'color picker',
      'banner style',
      'reset to defaults',
      'customize banner',
    ],
    lastUpdated: '2026-10-13',
    content: [
      {
        id: 'customize-appearance-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Appearance controls change the look of the banner that visitors see after you publish.',
      },
      {
        id: 'customize-appearance-video-section',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'See the banner restyled',
        icon: 'file-video',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'customize-appearance-video',
            type: FAQ_BLOCK_TYPES.VIDEO,
            videoId: '1223430875',
            hash: '7a06214149',
            title: 'Customize the banner appearance',
          },
        ],
      },
      {
        id: 'customize-appearance-visitors',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What each choice changes for visitors',
        icon: 'monitor',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'customize-appearance-visitors-preview',
            type: FAQ_BLOCK_TYPES.PREVIEW,
            variant: FAQ_PREVIEW_VARIANTS.APPEARANCE,
            items: [
              {
                id: 'customize-appearance-visitors-type',
                icon: 'layout-panel-top',
                control: 'type',
                title: 'Banner type',
                text: 'Switching between **Bar** and **Popup** also moves the banner: **Bar** lands at **Bottom**, **Popup** at **Center**.',
              },
              {
                id: 'customize-appearance-visitors-placement',
                icon: 'layout-dashboard',
                control: 'placement',
                title: 'Placement',
                text: '**Bar** sits at **Top** or **Bottom**. **Popup** sits at **Top Left**, **Top Right**, **Center**, **Bottom Left**, or **Bottom Right**.',
              },
              {
                id: 'customize-appearance-visitors-theme',
                icon: 'sun-moon',
                control: 'theme',
                title: 'Theme',
                text: 'Choosing a theme also sets the Preview page behind the banner so you can see how it looks: **Light** pairs with a light page, **Dark** with a dark page, and **Glass** with a **Mixed** page that shows the frosted surface on both.',
              },
              {
                id: 'customize-appearance-visitors-color',
                icon: 'palette',
                control: 'color',
                title: 'Brand color',
                text: 'This color is used for primary buttons and highlights on the banner. You pick from the shipped swatches - there is no free-form color input.',
              },
              {
                id: 'customize-appearance-visitors-overlay',
                icon: 'moon',
                title: 'Blocking overlay',
                text: 'The overlay dims the page until the visitor chooses, so the banner is harder to ignore. It does not block scripts - that is what [[category blocking|how-blocking-works]] does.',
              },
              {
                id: 'customize-appearance-visitors-preferences',
                icon: 'sliders-horizontal',
                title: 'Manage preferences button',
                text: 'When on, GDPR visitors can open category choices before they accept. The button appears only on the GDPR banner. Every banner still offers a **cookie details** link so visitors can see the used-cookies list without changing categories.',
              },
              {
                id: 'customize-appearance-visitors-branding',
                icon: 'sparkles',
                title: 'CookieArmor branding',
                text: 'When on, visitors see a small "Powered by CookieArmor" label on the banner.',
              },
            ],
          },
        ],
      },
      {
        id: 'customize-appearance-preview',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Preview is not the live site',
        icon: 'eye',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'customize-appearance-preview-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The **Preview regulation**, **Background**, and viewport (**Desktop** or **Mobile**) controls only change what you see here. They do not publish, and they do not change which banner a visitor actually sees - to change that, [[pick which regulations apply to which countries|choose-your-regulations]].',
          },
        ],
      },
      {
        id: 'customize-appearance-publish',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Publish or reset your changes',
        icon: 'send',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'customize-appearance-publish-intro',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Two buttons sit at the bottom of the form.',
          },
          {
            id: 'customize-appearance-publish-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'customize-appearance-publish-list-publish',
                text: '{btn:publish-changes} applies the draft and shows it to visitors. The button is enabled only after you change something from the last saved look.',
              },
              {
                id: 'customize-appearance-publish-list-reset',
                text: '{btn:reset} applies the product defaults to the live banner after asking you to confirm. It is a live change, not a draft undo. The defaults are a Bar at the Bottom, Light theme, Blue brand color, Blocking overlay off, Manage preferences on, CookieArmor branding on.',
              },
            ],
          },
        ],
      },
    ],
  },

  'read-banner-event-stats': {
    id: 'read-banner-event-stats',
    title: 'What event activity means',
    jumpTo: { name: ROUTES.ANALYTICS },
    description: 'What are Total Events, Opt-in Rate, Accept All, Reject All, and Custom?',
    keywords: [
      'event stats',
      'event metrics',
      'consent stats',
      'consent events',
      'consent rate',
      'lifetime stats',
      'all-time stats',
      'totals mismatch',
      'different totals',
      'banner stats',
      'banner metrics',
      'greyed out',
      'sparkline',
      'notice only',
    ],
    lastUpdated: '2026-10-14',
    content: [
      {
        id: 'rbes-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'What are **Total Events**, **Opt-in Rate**, **Accept All**, **Reject All**, and **Custom** - and why can the same banner show different totals on different surfaces?',
      },
      {
        id: 'rbes-increments',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What counts toward event activity',
        icon: 'chart-column',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'rbes-increments-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Cookie Armor records an event only when a visitor makes a choice on the banner - not when they view the page. The same counting rules apply on Banner Details, Analytics, and Overview.',
          },
          {
            id: 'rbes-increments-preview',
            type: FAQ_BLOCK_TYPES.PREVIEW,
            variant: FAQ_PREVIEW_VARIANTS.EVENT_STATS,
            caption: 'Example counts - not a live site.',
          },
          {
            id: 'rbes-increments-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            items: [
              {
                id: 'rbes-increments-accept',
                text: '**Accept All** increments when a visitor accepts every optional category in one action.',
              },
              {
                id: 'rbes-increments-reject',
                text: '**Reject All** increments when they reject every optional category in one action.',
              },
              {
                id: 'rbes-increments-custom',
                text: '**Custom** increments when a visitor enabled some optional categories and not others. Hover the **Custom** tile for a per-category breakdown - one visitor can allow more than one, so those counts do not add up to **Custom**.',
              },
              {
                id: 'rbes-increments-notice',
                text: 'A {regulation:notice} banner can increment **Accept All** when the visitor chooses **I Agree**. It never produces **Reject All** or **Custom** activity.',
              },
              {
                id: 'rbes-increments-close',
                text: '**Close** creates no event - that is not missing events.',
              },
            ],
          },
        ],
      },
      {
        id: 'rbes-scope-differs',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Why totals differ across surfaces',
        icon: 'funnel',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'rbes-scope-banner-overview',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'On the Banner Details page, **Total Events**, **Accept All**, **Reject All**, and **Custom** are all-time totals for that one banner, from the day it was created through today. There is no date range to change.',
          },
          {
            id: 'rbes-scope-analytics',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Analytics narrows the same totals to the banners and dates you pick. **Account Analytics** combines every banner in the account; selecting one banner limits the view to that site. The date range is **Last 7 days**, **Last 30 days**, **Last 90 days**, or a **Custom** span. On Account Analytics, the **Account info** card shows total banners and how many are **active** for the picked range - a banner counts as active when it has an **Opt-in Rate** for those dates.',
          },
          {
            id: 'rbes-scope-zero',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If totals stay at zero after visitors should have seen the banner, step through [[why activity can be missing|diagnose-missing-events]] - script, country, traffic, or a failed recording.',
          },
        ],
      },
      {
        id: 'rbes-opt-in-rate',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Opt-in Rate',
        icon: 'chart-pie',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'rbes-opt-in-rate-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '**Opt-in Rate** counts only **Accept All** events - a visitor who allowed some categories and not others is a **Custom** choice, not an opt-in. Cookie Armor shows a dash when **Total Events** is zero for the dates you picked.',
          },
        ],
      },
      {
        id: 'rbes-notice-only',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Notice-only banners',
        icon: 'megaphone',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'rbes-notice-only-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A banner with only {regulation:notice} offers one choice - **I Agree** - so only **Accept All** is ever recorded. **Reject All** and **Custom** stay at zero. **Opt-in Rate** can still read a percentage because every event is **Accept All**.',
          },
        ],
      },
    ],
  },

  'diagnose-missing-events': {
    id: 'diagnose-missing-events',
    title: 'Diagnose missing events',
    description:
      'Tell a real absence of banner choices from an install, country, traffic, or failed stats load.',
    keywords: [
      'zero events',
      'no activity',
      'missing events',
      'no banner',
      'script missing',
      'no visitors',
      'stats not loading',
      "couldn't load stats",
      'try again',
      'date range',
      'no choices recorded',
      'recording failed',
    ],
    lastUpdated: '2026-10-14',
    content: [
      {
        id: 'dme-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Figure out why banner event totals are missing or lower than expected.',
      },
      {
        id: 'dme-causes',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Why activity can be missing',
        icon: 'search-x',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'dme-causes-bullets',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            tone: CHECKLIST_TONES.NEGATIVE,
            items: [
              {
                id: 'dme-causes-install',
                text: '**Script missing** - when the install status is {status:script-missing}, visitors never see the banner and no choices are recorded. {status:couldnt-check} and {status:access-blocked} do not prove the banner is absent - [[walk through the install checks|install-the-script]] to confirm.',
              },
              {
                id: 'dme-causes-rotw',
                text: "**No banner for the visitor's country** - when [[Rest of the World is set to No banner|choose-your-regulations]], visitors outside your selected regulations see no banner and record no choice.",
              },
              {
                id: 'dme-causes-date-range',
                text: '**No events in the picked date range** - Analytics narrows totals to the dates you pick. Real choices can sit outside **Last 7 days**, **Last 30 days**, **Last 90 days**, or your **Custom** span - widen the range to confirm.',
              },
              {
                id: 'dme-causes-stats-load',
                text: '**Stats failed to load** - when the events stats card shows an error with **Try again** instead of a number, the figures did not load. Click **Try again** before treating a blank as a real zero.',
              },
            ],
          },
        ],
      },
      {
        id: 'dme-when-zero-real',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When a zero total is expected',
        icon: 'circle-check',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'dme-when-zero-real-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If none of the causes above applies, the zero is likely real. A new or quiet site stays at zero until visitors arrive and interact - [[only banner choices count as events|read-banner-event-stats]].',
          },
        ],
      },
    ],
  },

  'verify-consent-record': {
    id: 'verify-consent-record',
    title: 'Look up a consent record',
    description:
      'Open one saved banner choice by consent ID on the banner that owns it, read what the record shows, and tell a miss apart from a wrong ID.',
    keywords: ['proof of consent', 'audit trail'],
    lastUpdated: '2026-10-15',
    content: [
      {
        id: 'vcr-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Each saved banner choice has a consent ID. Enter one on the banner that owns it to see what the visitor chose and the context Cookie Armor recorded.',
      },
      {
        id: 'vcr-legal',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Not legal advice',
        text: 'A consent record shows what one visitor chose on your banner at that moment. Cookie Armor does not evaluate whether a given record satisfies a regulator for your site - what the law expects of consent evidence is in [[ePrivacy / cookie law|eprivacy-cookie-law]].',
      },
      {
        id: 'vcr-what-id',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What a consent ID is',
        icon: 'info',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'vcr-what-id-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A consent ID identifies one saved choice on one banner. It is never shared across banners or across your account, so a lookup only works on the banner where the visitor acted.',
          },
          {
            id: 'vcr-what-id-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If the same visitor reopens the banner and changes their mind, Cookie Armor saves that as a new choice with a new consent ID - the ID identifies one moment, not a visitor history.',
          },
          {
            id: 'vcr-what-id-p3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The lookup is separate from the live choice visitors carry on your site. That choice reads back as category permissions through [[the JavaScript API|javascript-api]] - it does not surface the consent ID.',
          },
        ],
      },
      {
        id: 'vcr-lookup',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Look up a record',
        icon: 'scan-search',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'vcr-lookup-where',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "The **Look up consent** card sits on each banner's **Overview** tab, and on **Analytics** once you pick a single banner from the sidebar. **Account Analytics** combines every banner, so the card is hidden there - pick one banner first.",
          },
          {
            id: 'vcr-lookup-steps',
            type: FAQ_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'vcr-lookup-step-banner',
                title: 'Open the banner that owns the ID',
                text: 'Lookups work on one banner at a time. If you are not sure which banner saved a given ID, start with the one visitors see for that region or regulation.',
                jumpTo: { name: ROUTES.ANALYTICS },
              },
              {
                id: 'vcr-lookup-step-id',
                title: 'Paste the consent ID and run the lookup',
                text: 'The ID is a UUID. {btn:look-up-consent} runs once the field holds a valid one.',
              },
            ],
          },
        ],
      },
      {
        id: 'vcr-record',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What a record shows',
        icon: 'file-text',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'vcr-record-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "A found record opens as a **Consent record** dialog with two panels: **Record & context** holds the context Cookie Armor saved, and **Visitor's choice** holds what the visitor allowed.",
          },
          {
            id: 'vcr-record-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Field', 'What it shows'],
            rows: [
              ['**Consent ID**', 'The exact ID you looked up.'],
              ['**Timestamp**', 'When the choice was saved, in UTC.'],
              [
                '**Version**',
                'The banner version the visitor saw - useful when you have changed categories or copy since.',
              ],
              [
                '**Regulation**',
                'The ruleset applied for this visitor - {regulation:gdpr}, {regulation:ccpa}, or {regulation:notice}.',
              ],
              [
                '**Domain**',
                'The site the visitor was on. Clicking it opens that site in a new tab.',
              ],
              ['**Country**', "The visitor's country, resolved from the code Cookie Armor saw."],
              [
                '**IP address** and **user agent**',
                'Personal data, so the lookup dialog is the only place in Cookie Armor that shows them - Analytics charts and lists do not.',
              ],
              [
                '**Decision**',
                '**Accept All** when every optional category was allowed, **Reject All** when none was, otherwise **Custom**.',
              ],
              [
                '**By category**',
                'Each optional category is **Accepted** or **Rejected**. **Necessary** is required for the site to work and shows as **Always on** - it does not affect the Decision.',
              ],
            ],
          },
          {
            id: 'vcr-record-copy',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '{btn:copy-consent-record} copies both panels - including the IP address and user agent - as plain text.',
          },
        ],
      },
      {
        id: 'vcr-not-found',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When no record is found',
        icon: 'search-x',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'vcr-not-found-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A **Consent not found** result means this banner does not have a saved choice with that ID. The dialog itself names the two things to check:',
          },
          {
            id: 'vcr-not-found-causes',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            tone: CHECKLIST_TONES.NEGATIVE,
            items: [
              {
                id: 'vcr-not-found-wrong-id',
                text: 'The consent ID is wrong - a typo, an extra space, or a trimmed character stops the match.',
              },
              {
                id: 'vcr-not-found-wrong-banner',
                text: 'The ID belongs to a different banner - try the lookup on another banner you own.',
              },
            ],
          },
          {
            id: 'vcr-not-found-events',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If no banner you own returns a match, read [[why banner activity can go missing|diagnose-missing-events]] - the install, country, traffic, and stats-load checks apply here too.',
          },
        ],
      },
    ],
  },

  'google-consent-mode-setting': {
    id: 'google-consent-mode-setting',
    title: 'Google Consent Mode setting',
    description:
      'Google Consent Mode lets Cookie Armor write Google tag consent from the banner choice. It is on by default when you create a banner, and later changes go live when you publish them.',
    keywords: [
      'GCM',
      'gtag',
      'Google tags',
      'consent mode',
      'Google Consent Mode',
      'Google tags not updating',
      'two systems updating consent',
      'GCM on or off',
      'who writes consent',
    ],
    lastUpdated: '2026-10-16',
    content: [
      {
        id: 'gcm-setting-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Google Consent Mode decides whether Cookie Armor writes Google tag consent for this banner. It is a measurement setting - [[what Google Consent Mode is|what-is-google-consent-mode]] has the Google side of the story.',
      },
      {
        id: 'gcm-setting-decide',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Decide who writes Google tag consent',
        icon: 'zap',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'gcm-setting-decide-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The site needs exactly one source for Google tag consent. Leave it **on** when Google tags on this site (Google Analytics, Google Ads, and others) should follow the banner choice; Cookie Armor then writes those signals for every visitor. Switch it **off** only when another system is already writing Consent Mode for this site. Two sources writing the same signals overwrite each other.',
          },
          {
            id: 'gcm-setting-decide-off',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Off is silence, not a grant - Cookie Armor writes nothing, and Google tags fall back to their own defaults.',
          },
          {
            id: 'gcm-setting-decide-blocking',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Script blocking is a separate setting - [[how blocking works|how-blocking-works]] covers that. Google Consent Mode hosts are never held as scripts, even when this setting is off.',
          },
        ],
      },
      {
        id: 'gcm-setting-when-live',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When your choice takes effect',
        icon: 'calendar-days',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'gcm-setting-when-live-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Google Consent Mode is on by default when you create a banner, and you can switch it off in the same step. Whichever value you confirm is live as soon as the banner is created. Later changes go live when you click {btn:publish-changes} on [[the Behaviour tab|configure-banner-behaviour]].',
          },
        ],
      },
    ],
  },

  'delete-a-banner': {
    id: 'delete-a-banner',
    title: 'Delete a banner',
    description:
      'Deleting a banner cannot be undone. The site script stays until you remove it, and the same URL can be created again only after this banner is gone.',
    keywords: [
      'remove banner',
      'permanently delete',
      'cannot be undone',
      'no restore',
      'uninstall banner',
      'delete site',
      'reuse URL',
      'free up domain',
    ],
    lastUpdated: '2026-10-09',
    content: [
      {
        id: 'delete-a-banner-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: "When a banner is no longer needed, you can permanently delete it from your account. You'll lose access to its settings and data, and this cannot be undone - but your account records and compliance history are retained.",
      },
      {
        id: 'delete-a-banner-confirm',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Delete a banner - step by step',
        icon: 'list-todo',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'delete-a-banner-confirm-steps',
            type: FAQ_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'delete-a-banner-step-open',
                title: "Open the banner's Overview",
                text: 'From **Your Banners**, open the banner you want to delete and go to the **Overview** tab.',
              },
              {
                id: 'delete-a-banner-step-find-card',
                title: 'Open the delete card',
                text: 'Scroll to the bottom of Overview, find **No longer using this banner?**, and click {btn:delete-banner}.',
              },
              {
                id: 'delete-a-banner-step-type-name',
                title: 'Type the Banner Name to enable Delete',
                text: "Type the banner's name exactly as it appears - the match is case-sensitive.",
              },
              {
                id: 'delete-a-banner-step-delete',
                title: 'Permanently delete',
                text: 'Click {btn:delete} to remove this banner from your account.',
              },
            ],
          },
        ],
      },
      {
        id: 'delete-a-banner-cannot-undo',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'After you delete',
        icon: 'trash',
        color: FAQ_SECTION_ICON_TONES.NEGATIVE,
        blocks: [
          {
            id: 'delete-a-banner-after-script-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'alert-triangle',
            label: 'Your site still has the script',
            text: "Deleting the banner doesn't touch your website's code. Remove the [[Cookie Armor script|install-the-script]] from your HTML or tag manager yourself, or it will keep loading on every page.",
          },
          {
            id: 'delete-a-banner-after-reuse',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If you want a banner on this URL again, you can [[create a new one|create-a-banner]] once this delete finishes.',
          },
        ],
      },
    ],
  },
}
